import { useState, useEffect, useCallback } from "react";
import { detectIsPortrait } from "../../utils/orientationUtils";

type Orientation = "portrait" | "landscape";

interface OrientationState {
  isPortrait: boolean;
  orientation: Orientation;
  angle: number;
}

const useDeviceOrientation = (): OrientationState => {
  const [orientation, setOrientation] = useState<OrientationState>(() => ({
    isPortrait: getInitialOrientation(),
    orientation: getInitialOrientationType(),
    angle: window.screen.orientation?.angle || 0,
  }));

  const handleOrientationChange = useCallback(() => {
    const isPortrait = detectIsPortrait();
    const angle = window.screen.orientation?.angle || 0;

    setOrientation({
      isPortrait,
      orientation: isPortrait ? "portrait" : "landscape",
      angle,
    });
  }, []);

  useEffect(() => {
    // Method 1: Screen Orientation API (modern, most reliable)
    if (window.screen.orientation) {
      window.screen.orientation.addEventListener("change", handleOrientationChange);

      return () => {
        window.screen.orientation?.removeEventListener("change", handleOrientationChange);
      };
    }
    // Method 2: Fallback to orientationchange event
    else if ("onorientationchange" in (window as object)) {
      window.addEventListener("orientationchange", handleOrientationChange);

      return () => {
        window.removeEventListener("orientationchange", handleOrientationChange);
      };
    }
    // Method 3: Last resort - matchMedia + debounced resize as supplement
    else {
      const mediaQuery = window.matchMedia("(orientation: portrait)");
      const handleMediaChange = () => handleOrientationChange();

      let resizeTimer: NodeJS.Timeout;
      const handleResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(handleOrientationChange, 100);
      };

      mediaQuery.addEventListener("change", handleMediaChange);
      window.addEventListener("resize", handleResize);

      return () => {
        mediaQuery.removeEventListener("change", handleMediaChange);
        clearTimeout(resizeTimer);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [handleOrientationChange]);

  return orientation;
};

function getInitialOrientation(): boolean {
  return detectIsPortrait();
}

function getInitialOrientationType(): Orientation {
  if (window.screen.orientation) {
    return window.screen.orientation.type.includes("portrait") ? "portrait" : "landscape";
  }
  return window.innerHeight > window.innerWidth ? "portrait" : "landscape";
}

export default useDeviceOrientation;
